if (!customElements.get('product-visual-upholstery')) {
  customElements.define(
    'product-visual-upholstery',
    class ProductVisualUpholstery extends HTMLElement {
      connectedCallback() {
        if (this.dataset.configurator === 'true') {
          this.connectConfigurator();
          return;
        }
        this.connectLegacy();
      }

      disconnectedCallback() {
        if (this.dataset.configurator !== 'true') return;
        this.listeners?.abort();
        this.configuratorReady = false;
      }

      connectLegacy() {
        if (this.dataset.ready === 'true') return;
        this.dataset.ready = 'true';
        this.buttons = Array.from(this.querySelectorAll('[data-visual-upholstery-option]'));
        this.activeName = this.querySelector('[data-visual-upholstery-active-name]');
        this.productSection = this.closest('[id^="MainProduct-"]');
        this.gallery = this.productSection?.querySelector('media-gallery');
        this.viewer = this.gallery?.querySelector('[id^="GalleryViewer"]');
        if (!this.buttons.length || !this.gallery || !this.viewer) return;

        customElements.whenDefined('media-gallery').then(() => this.initialize());
      }

      initialize() {
        this.primarySlide = this.viewer.querySelector('.product__media-item.is-active') ||
          this.viewer.querySelector('[data-media-id]');
        this.primaryImage = this.primarySlide?.querySelector('.product__media img');
        this.primaryZoomTrigger = this.primarySlide?.querySelector('[data-product-zoom-trigger]');
        this.primaryMediaId = this.primarySlide?.dataset.mediaId;
        this.primaryThumbnail = Array.from(this.gallery.querySelectorAll('[data-target]'))
          .find((thumbnail) => thumbnail.dataset.target === this.primaryMediaId);
        this.primaryThumbnailImage = this.primaryThumbnail?.querySelector('img');
        if (!this.primarySlide || !this.primaryImage || !this.primaryZoomTrigger || !this.primaryMediaId) return;

        this.original = this.captureOriginalState();
        this.buttons.forEach((button) => {
          button.addEventListener('click', () => this.selectOption(button));
        });

        this.gallery.addEventListener('click', (event) => {
          if (event.target.closest('[data-target]')) this.clearSelection(true);
        }, true);

        this.viewer.addEventListener('slideChanged', (event) => {
          const currentMediaId = event.detail.currentElement?.dataset.mediaId;
          if (currentMediaId && currentMediaId !== this.primaryMediaId) this.clearSelection(true);
        });
      }

      connectConfigurator() {
        if (this.configuratorReady) return;
        this.configuratorReady = true;
        this.listeners = new AbortController();
        const listenerOptions = { signal: this.listeners.signal };
        this.labels = this.querySelector('[data-configurator-settings]').dataset;
        this.buttons = Array.from(this.querySelectorAll('[data-visual-upholstery-option]'));
        this.activeName = this.querySelector('[data-visual-upholstery-active-name]');
        this.productSection = this.closest('[id^="MainProduct-"]');
        this.gallery = this.productSection?.querySelector('media-gallery');
        this.viewer = this.gallery?.querySelector('[id^="GalleryViewer"]');
        this.finishControl = this.querySelector('[data-configurator-finish]');
        this.seatsControl = this.querySelector('[data-configurator-seats]');
        this.seatCountControl = this.querySelector('[data-configurator-seat-count]');
        this.commentsControl = this.querySelector('[data-configurator-comments]');
        this.quoteLink = this.querySelector('[data-configurator-whatsapp]');
        this.configuration ||= { option: null, finish: 'reference', seats: 'reference', count: '', comments: '', photo: 'idle' };
        this.buttons.forEach((button) => {
          button.addEventListener('click', () => {
            this.configuration.option = button;
            this.showConfiguredImage();
            this.renderConfiguration();
          }, listenerOptions);
        });
        const onInput = () => {
          Object.assign(this.configuration, {
            finish: this.finishControl.value,
            seats: this.seatsControl.value,
            count: this.seatCountControl.value,
            comments: this.commentsControl.value.trim(),
          });
          this.renderConfiguration();
        };
        this.addEventListener('input', onInput, listenerOptions);
        this.addEventListener('change', onInput, listenerOptions);
        this.quoteLink.addEventListener('click', (event) => {
          this.renderConfiguration();
          if (this.quoteLink.getAttribute('aria-disabled') === 'true') {
            event.preventDefault();
            this.seatCountControl.reportValidity();
            this.seatCountControl.focus();
          }
        }, listenerOptions);
        this.querySelector('[data-configurator-controls]').disabled = false;
        this.prepareConfiguredGallery();
        this.viewer?.addEventListener('slideChanged', (event) => {
          if (!event.detail.currentElement) return;
          this.configuration.viewOther = event.detail.currentElement.dataset.mediaId !== this.primaryMediaId;
          this.renderConfiguration();
        }, listenerOptions);
        this.gallery?.addEventListener('click', (event) => {
          const thumbnail = event.target.closest('[data-target]');
          if (thumbnail) {
            this.configuration.viewOther = thumbnail.dataset.target !== this.primaryMediaId;
            this.renderConfiguration();
          }
        }, listenerOptions);
        this.renderConfiguration();
      }

      prepareConfiguredGallery() {
        this.primarySlide = this.viewer?.querySelector('.product__media-item.is-active') ||
          this.viewer?.querySelector('[data-media-id]');
        this.primaryImage = this.primarySlide?.querySelector('.product__media img');
        this.primaryZoomTrigger = this.primarySlide?.querySelector('[data-product-zoom-trigger]');
        this.primaryMediaId = this.primarySlide?.dataset.mediaId;
        this.primaryThumbnail = Array.from(this.gallery?.querySelectorAll('[data-target]') || [])
          .find((thumbnail) => thumbnail.dataset.target === this.primaryMediaId);
        this.primaryThumbnailImage = this.primaryThumbnail?.querySelector('img');
        if (this.primaryImage && this.primaryZoomTrigger && !this.original) this.original = this.captureOriginalState();
      }

      showConfiguredImage() {
        const option = this.configuration.option;
        this.configuration.viewOther = false;
        if (!this.original || !option?.dataset.imageSrc) {
          this.configuration.photo = 'missing';
          if (this.original) this.restoreOriginalImage();
          this.activateReferenceMedia();
          return;
        }
        this.configuration.photo = 'loading';
        const loader = new Image();
        loader.onload = () => {
          if (!this.isConnected || this.configuration.option !== option) return;
          this.applyOptionImage(option);
          this.configuration.photo = 'ready';
          this.activateReferenceMedia();
          this.renderConfiguration();
        };
        loader.onerror = () => {
          if (!this.isConnected || this.configuration.option !== option) return;
          this.restoreOriginalImage();
          this.configuration.photo = 'error';
          this.activateReferenceMedia();
          this.renderConfiguration();
        };
        loader.src = option.dataset.imageSrc;
      }

      activateReferenceMedia() {
        if (this.primaryMediaId && typeof this.gallery?.setActiveMedia === 'function') {
          this.gallery.setActiveMedia(this.primaryMediaId, false);
        }
      }

      renderConfiguration() {
        const state = this.configuration;
        const data = this.labels;
        const upholstery = state.option?.dataset.optionName || data.pending;
        const finish = state.finish === 'other'
          ? data.otherFinish.replace('{reference}', data.referenceFinish || data.pending)
          : data.referenceFinish || data.pending;
        this.querySelector('[data-seat-request]').hidden = state.seats !== 'other';
        this.seatCountControl.required = state.seats === 'other';
        const validCount = state.seats !== 'other' ||
          (this.seatCountControl.checkValidity() && Number.isInteger(Number(state.count)));
        const seats = state.seats === 'other'
          ? data.otherSeats.replace('{count}', state.count || data.pending).replace('{reference}', data.referenceSeats || data.pending)
          : data.referenceSeats || data.pending;
        const values = { upholstery, finish, seats, comments: state.comments || data.none };
        this.buttons.forEach((button) => button.setAttribute('aria-pressed', String(button === state.option)));
        if (this.activeName) this.activeName.textContent = upholstery;
        Object.entries(values).forEach(([key, value]) => {
          this.querySelector(`[data-summary="${key}"]`).textContent = value;
        });
        const special = state.finish === 'other' || state.seats === 'other' || Boolean(state.comments);
        this.querySelector('[data-special-notice]').hidden = !special;
        this.querySelector('[data-final-price-pending]').hidden = !special;
        this.classList.toggle('dm-configurator--special', special);
        this.querySelector('[data-configurator-error]').hidden = validCount;
        this.querySelector('[data-gallery-note]').hidden = !state.option || !state.viewOther;
        const photoStatus = this.querySelector('[data-photo-status]');
        photoStatus.hidden = Boolean(state.option && state.viewOther);
        if (state.photo !== 'idle') {
          const photoText = state.photo === 'ready' ? data.photoReady : state.photo === 'loading'
            ? data.loading : state.photo === 'error' ? data.photoError : data.missingPhoto;
          if (photoStatus.textContent !== photoText) photoStatus.textContent = photoText;
        }
        if (!validCount) {
          this.quoteLink.removeAttribute('href');
          this.quoteLink.setAttribute('aria-disabled', 'true');
          this.quoteLink.setAttribute('tabindex', '0');
          return;
        }
        this.quoteLink.removeAttribute('aria-disabled');
        this.quoteLink.removeAttribute('tabindex');
        const message = [data.greeting, '',
          `${data.labelUpholstery}: ${values.upholstery}`,
          `${data.labelFinish}: ${values.finish}`,
          `${data.labelSeats}: ${values.seats}`,
          `${data.labelComments}: ${values.comments}`,
          `${data.labelPrice}: ${data.basePrice}`, '', data.confirm, '',
          `${data.labelProduct}: ${data.productUrl}`,
        ].join('\n');
        this.quoteLink.href = `https://wa.me/525548780435?text=${encodeURIComponent(message)}`;
      }

      captureOriginalState() {
        return {
          image: this.captureImageState(this.primaryImage),
          thumbnail: this.captureImageState(this.primaryThumbnailImage),
          zoomSrc: this.primaryZoomTrigger.dataset.productZoomSrc,
          zoomAlt: this.primaryZoomTrigger.dataset.productZoomAlt,
        };
      }

      captureImageState(image) {
        if (!image) return null;
        return {
          src: image.getAttribute('src'),
          srcset: image.getAttribute('srcset'),
          sizes: image.getAttribute('sizes'),
          width: image.getAttribute('width'),
          height: image.getAttribute('height'),
          alt: image.getAttribute('alt'),
        };
      }

      selectOption(button) {
        if (typeof this.gallery.setActiveMedia !== 'function') return;
        this.gallery.setActiveMedia(this.primaryMediaId, false);
        this.applyOptionImage(button);
        this.buttons.forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
        if (this.activeName) this.activeName.textContent = button.dataset.optionName;
      }

      applyOptionImage(button) {
        this.applyImageState(this.primaryImage, {
          srcset: button.dataset.imageSrcset,
          src: button.dataset.imageSrc,
          width: button.dataset.imageWidth,
          height: button.dataset.imageHeight,
          alt: button.dataset.imageAlt,
        });
        this.applyImageState(this.primaryThumbnailImage, {
          srcset: button.dataset.thumbnailSrcset,
          src: button.dataset.thumbnailSrc,
          alt: button.dataset.imageAlt,
        });
        this.primaryZoomTrigger.dataset.productZoomSrc = button.dataset.zoomSrc;
        this.primaryZoomTrigger.dataset.productZoomAlt = button.dataset.imageAlt;
      }

      clearSelection(restoreImage) {
        if (restoreImage) this.restoreOriginalImage();
        this.buttons.forEach((button) => button.setAttribute('aria-pressed', 'false'));
        if (this.activeName) this.activeName.textContent = this.dataset.emptyLabel;
      }

      restoreOriginalImage() {
        this.applyImageState(this.primaryImage, this.original.image);
        this.applyImageState(this.primaryThumbnailImage, this.original.thumbnail);
        this.primaryZoomTrigger.dataset.productZoomSrc = this.original.zoomSrc;
        this.primaryZoomTrigger.dataset.productZoomAlt = this.original.zoomAlt;
      }

      applyImageState(image, state) {
        if (!image || !state) return;
        Object.entries(state).forEach(([name, value]) => this.setOrRemoveAttribute(image, name, value));
      }

      setOrRemoveAttribute(element, name, value) {
        if (value === null || value === undefined) {
          element.removeAttribute(name);
        } else {
          element.setAttribute(name, value);
        }
      }
    }
  );
}
